import React, { useState } from "react";

import Select from "../../../../components/input/select/Select";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import AutoCompleteTextField from "../../../../components/input/autoComplete/AutoCompleteTextField";

import {
  FormSection,
  FormRow,
} from "../../../../components/layout/forms/Index";

import { countriesDataset } from "../../../../assets/dataset/CountriesDataset";
import { findAutocompleteOption } from "../../../../utils/helpers/autoComplete/autoCompleteHelper";
import { generateUniqueId } from "../../../../utils/helpers/generator/generateUniqueId";

import TalentModal from "./TalentModal";
import { clean, getApiError } from "../utils/talentUtils";

import "./TalentForms.css";

const visibilityOptions = [
  { label: "Private", value: "Private" },
  { label: "Public", value: "Public" },
];

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Prefer not to say", value: "none" },
];

const initialFields = (profile) => ({
  talentTag: profile?.talentTag || generateUniqueId(4).toUpperCase(),
  bio: profile?.bio || "",
  gender: profile?.gender || "",
  birthMonth: profile?.birthMonth || "",
  nationality: profile?.nationality || "",
  country: profile?.country || "",
  state: profile?.state || "",
  height: profile?.height ?? "",
  weight: profile?.weight ?? "",
  visibility: profile?.visibility || "Private",
});

const validBirthMonth = (value) => {
  if (!value) return true;

  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return false;
  }

  const now = new Date();
  const currentMonth = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
  ].join("-");

  return value <= currentMonth && Number(value.slice(0, 4)) >= 1900;
};

export default function TalentProfileForm({
  initial,
  saving = false,
  onSave,
  onClose,
}) {
  const [fields, setFields] = useState(() => initialFields(initial));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const change = ({ target }) => {
    const { name, value } = target;

    setFields((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    setError("");
  };

  const changeMeasurement = (event) => {
    const value = event.target.value;

    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      change(event);
    }
  };

  const save = async () => {
    if (saving) return;

    const next = {};

    const talentTag = clean(fields.talentTag).toUpperCase();
    const bio = clean(fields.bio);

    if (!initial) {
      if (!/^[A-Z0-9_-]{1,50}$/.test(talentTag)) {
        next.talentTag = "Enter a valid Talent ID.";
      }
    }

    if (!validBirthMonth(fields.birthMonth)) {
      next.birthMonth = "Enter a valid month and year.";
    }

    const country = fields.country
      ? findAutocompleteOption(countriesDataset, fields.country)
      : null;

    if (fields.country && !country) {
      next.country = "Select a country from the suggestions.";
    }

    for (const field of ["height", "weight"]) {
      const value = fields[field];

      if (
        value !== "" &&
        (!Number.isFinite(Number(value)) ||
          Number(value) <= 0 ||
          Number(value) > 999.99)
      ) {
        next[field] = "Enter a valid measurement.";
      }
    }

    if (!visibilityOptions.some(({ value }) => value === fields.visibility)) {
      next.visibility = "Select a valid visibility.";
    }

    setErrors(next);

    if (Object.keys(next).length) return;

    const payload = {
      bio,
      gender: fields.gender || null,
      birthMonth: fields.birthMonth || null,
      nationality: clean(fields.nationality) || null,
      country: country?.value || null,
      state: clean(fields.state) || null,
      height: fields.height === "" ? null : Number(fields.height),
      weight: fields.weight === "" ? null : Number(fields.weight),
      visibility: fields.visibility,
    };

    if (!initial) {
      payload.talentTag = talentTag;
    }

    try {
      setError("");
      await onSave(payload);
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <TalentModal
      title={initial ? "Edit Talent ID" : "Create Talent ID"}
      description={
        initial
          ? "Update your personal details and sporting biography."
          : "Create your sporting identity across all sports."
      }
      busy={saving}
      onClose={onClose}
      onSave={save}
      saveLabel={initial ? "Save Changes" : "Create Talent ID"}
    >
      {error && (
        <p className="user-talent__error" role="alert">
          {error}
        </p>
      )}

      <FormSection>
        <FormRow>
          <Select
            label="Gender"
            name="gender"
            value={fields.gender}
            onChange={change}
            items={genderOptions}
            defaultOption="Select gender (optional)"
          />

          <TextField
            label="Month / Year Born"
            type="month"
            name="birthMonth"
            value={fields.birthMonth}
            onChange={change}
          />

          <TextField
            label="Nationality"
            name="nationality"
            value={fields.nationality}
            onChange={change}
            maxLength={100}
            placeholder="Optional"
          />

          <AutoCompleteTextField
            label="Country"
            name="country"
            value={fields.country}
            onChange={change}
            options={countriesDataset}
            error={errors.country}
            placeholder="Start typing a country..."
            maxLength={100}
          />

          <TextField
            label="State / Region"
            name="state"
            value={fields.state}
            onChange={change}
            maxLength={100}
          />

          <Select
            label="Visibility"
            name="visibility"
            value={fields.visibility}
            onChange={change}
            items={visibilityOptions}
            error={errors.visibility}
            required
          />

          <TextField
            label="Height (cm)"
            name="height"
            value={fields.height}
            onChange={changeMeasurement}
            placeholder="Optional"
          />

          <TextField
            label="Weight (kg)"
            name="weight"
            value={fields.weight}
            onChange={changeMeasurement}
            placeholder="Optional"
          />
        </FormRow>

        <TextArea
          label="Sporting Biography"
          name="bio"
          value={fields.bio}
          onChange={change}
          maxLength={10000}
          maxHeight={140}
          placeholder="Tell others about your sporting journey."
        />

        <p className="talent-form__note">
          Personal details are optional. Your Talent ID remains private unless
          you choose Public.
        </p>
      </FormSection>
    </TalentModal>
  );
}
